import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Shares the horizontal collapse (icon-rail) state between the Sidebar and the
 * header hamburger toggle, which live in different parts of the tree.
 * Persisted across reloads.
 *
 * Below `lg` the sidebar is a drawer instead; `mobileOpen` is its open state.
 * Not persisted — a drawer should never reopen by itself after a reload.
 */
const SidebarContext = createContext(null);

export const SidebarProvider = ({ children }) => {
	const [collapsed, setCollapsed] = useState(
		() => localStorage.getItem("sidebarCollapsed") === "true"
	);

	useEffect(() => {
		localStorage.setItem("sidebarCollapsed", String(collapsed));
	}, [collapsed]);

	const [mobileOpen, setMobileOpen] = useState(false);

	const toggleCollapsed = () => setCollapsed((c) => !c);
	const openMobile = () => setMobileOpen(true);
	const closeMobile = useCallback(() => setMobileOpen(false), []);

	return (
		<SidebarContext.Provider
			value={{ collapsed, toggleCollapsed, mobileOpen, openMobile, closeMobile }}
		>
			{children}
		</SidebarContext.Provider>
	);
};

// Falls back to a no-op when used outside a provider so consumers never crash.
export const useSidebar = () =>
	useContext(SidebarContext) || {
		collapsed: false,
		toggleCollapsed: () => {},
		mobileOpen: false,
		openMobile: () => {},
		closeMobile: () => {},
	};
